import type { StripeError } from '@stripe/stripe-js';

// `@stripe/stripe-js` types `code` as a plain string, so these values are pinned by the unit tests.
// IBAN Element input errors. stripeErrorI18nKey shows all of them as the account-number field message.
export const STRIPE_IBAN_INPUT_ERROR_CODES: readonly string[] = [
	'incomplete_iban',
	'invalid_iban',
	'invalid_iban_start',
	'invalid_iban_country_code',
	'unsupported_non_sepa_iban_country_code',
];

// The card codes are the ones the card Elements raise with the postal code hidden.
const EXPECTED_VALIDATION_ERROR_CODES: readonly string[] = [
	'incomplete_number',
	'invalid_number',
	'incomplete_cvc',
	'invalid_cvc',
	'incomplete_expiry',
	'invalid_expiry_month',
	'invalid_expiry_month_past',
	'invalid_expiry_year',
	'invalid_expiry_year_past',
	...STRIPE_IBAN_INPUT_ERROR_CODES,
	// The bank rejected an IBAN that passed the IBAN Element's format checks.
	'invalid_bank_account_iban',
];

const EXPECTED_STRIPE_ERROR_CODES: readonly string[] = [
	'payment_intent_authentication_failure',
	'setup_intent_authentication_failure',
];

/**
 * Determines whether a Stripe error is an expected user outcome rather than an unexpected failure.
 *
 * Expected outcomes are:
 * - `validation_error` with an incomplete or invalid card/IBAN input code. Other validation errors are
 *   unexpected, because Stripe.js also raises `validation_error` for invalid arguments to its own methods.
 * - `card_error`: the card was declined.
 * - `payment_intent_authentication_failure` / `setup_intent_authentication_failure`: the user failed or
 *   abandoned a 3DS challenge. Matched by code because Stripe can report them as `invalid_request_error`.
 *
 * Anything else (network, API, rate limit, authentication, idempotency, other invalid requests) is unexpected.
 */
export function isExpectedStripeError({ type, code }: Pick<StripeError, 'type' | 'code'>): boolean {
	if (type === 'card_error') return true;
	if (code === undefined) return false;
	if (type === 'validation_error') return EXPECTED_VALIDATION_ERROR_CODES.includes(code);
	return EXPECTED_STRIPE_ERROR_CODES.includes(code);
}
